import React, { useCallback, useEffect, useReducer } from 'react'
import AuthContext from './authContext'
import AuthReducer from './authReducer'
import {
    apiCall,
    setAuthToken,
    resetSessionTimeoutLogout,
    setSessionTimeoutHandler,
} from '../../common/api'
import { response } from '../common'
import {
    USER_LOADED,
    GET_2FA_USER_DETAILS,
    LOGOUT,
    RESPONSE_STATUS,
    CLEAR_RESPONSE,
} from './authTypes'

const AuthState = (props) => {
    // Which auth delivery mode the server used for THIS login is decided
    // server-side (see issueAdminAuthToken() in carone-api's admin
    // controller): it sets an HttpOnly cookie and omits the token from the
    // response body, OR it omits the cookie and includes the token in the
    // body. Reading `commonContext.configFeatures.httponly_cookie_auth` here
    // to guess the same thing was a race: that flag loads asynchronously via
    // getGlobalVariable() on app mount and is not awaited before the login
    // form can be submitted, so on a slow/loaded backend (observed 1s-80s+
    // for getDefault on this environment) the login response routinely comes
    // back and completes before the flag ever loads - the login() call
    // guessed cookie-mode ON, silently discarded the token the server had
    // actually issued in the body, and every request after login (starting
    // with the immediate loadUser()) then had neither a cookie nor a Bearer
    // token, got a 403 "No Bearer Token Available!" and bounced back to
    // /login. Keying off the token's mere presence in the response is the
    // authoritative, race-free signal instead: the server only ever puts a
    // token in the body when it did NOT set an auth cookie.
    const applyLoginToken = (data) => {
        // Legacy servers return data.token instead of data.responseData.token.
        const token = data?.responseData?.token || data?.token
        resetSessionTimeoutLogout()
        if (token) {
            localStorage.setItem('token', token)
            setAuthToken(token)
        } else {
            localStorage.removeItem('token')
            setAuthToken(null)
        }
    }

    const initialState = {
        token: null, // Token is now stored in HttpOnly cookie, not accessible to JS
        isAuthenticated: null,
        isAdmin: null,
        loading: true,
        user: {},
        twoFactor_details: {
            record: [],
        },
        responseStatus: null,
    }

    const [state, dispatch] = useReducer(AuthReducer, initialState)
    let resp = new response(dispatch, RESPONSE_STATUS)

    // Register User
    const register = async (formData) => {
        try {
            const [res] = await Promise.all([
                apiCall('post', 'register', formData, '', ''),
            ])
            resp.commonResponse(res.data, 'register')
        } catch (err) {
            resp.commonErrorResponse('register')
        }
    }

    // Check Email User
    const checkValidation = async (formData) => {
        try {
            const [res] = await Promise.all([
                apiCall('post', 'checkValidation', formData, '', 'user'),
            ])
            resp.commonResponse(res.data, 'checkValidation')
        } catch (err) {
            resp.commonErrorResponse('checkValidation')
        }
    }

    // Login User
    const login = async (formData) => {
        try {
            const [res] = await Promise.all([
                apiCall('post', 'login', formData, '', ''),
            ])
            resp.commonResponse(res.data, 'login')
            // 21/22/23 = "2FA Enabled. OTP Required!" - no session issued yet.
            const otpPending = [21, 22, 23].includes(
                Number(res.data?.data?.responseType)
            )
            if (res.data.status === 'success' && !otpPending) {
                applyLoginToken(res.data?.data)
                loadUser()
            }
        } catch (err) {
            resp.commonErrorResponse('login')
        }
    }

    //2Factor Verification
    const get2faDetails = async (formData) => {
        try {
            const [res] = await Promise.all([
                apiCall('post', 'get2faDetails', formData, '', ''),
            ])
            const from = '2faDetails'
            if (res.data.status === 'success') {
                dispatch({
                    type: GET_2FA_USER_DETAILS,
                    payload: {
                        record: res.data.data.responseData.length
                            ? res.data.data.responseData
                            : [],
                    },
                })
            } else if (res.data.status === 'error') {
                resp.commonResponse(res.data, from)
            } else {
                resp.commonErrorResponse(from)
            }
        } catch (err) {
            resp.commonErrorResponse('2fatoken')
        }
    }

    // Switch User
    const switchUser = async (formData) => {
        try {
            const [res] = await Promise.all([
                apiCall('post', 'switchuser', formData, '', ''),
            ])
            resp.commonResponse(res.data, 'switchuser')
            if (res.data.status === 'success') {
                applyLoginToken(res.data?.data)
                loadUser()
            }
        } catch (err) {
            resp.commonErrorResponse('switchuser')
        }
    }

    // Load User
    const loadUser = async () => {
        // Authentication uses the bearer header or HttpOnly session cookie.
        const [res] = await Promise.all([
            apiCall('post', 'validateUser', {}, '', ''),
        ])
        if (res.data.status === 'success') {
            dispatch({
                type: USER_LOADED,
                payload: {
                    data: res.data.data.responseData,
                },
            })
        } else if (res.data.status === 'error') {
            dispatch({
                type: LOGOUT,
            })
        } else {
            dispatch({
                type: LOGOUT,
            })
        }
    }

    const updateProfile = async (formData, noAlert) => {
        try {
            const [res] = await Promise.all([
                apiCall('post', 'updateProfile', formData, '', ''),
            ])
            resp.commonResponse(res.data, 'updateProfile', noAlert)
        } catch (err) {
            resp.commonErrorResponse('updateProfile', noAlert)
        }
    }

    const updatePreference = async (formData) => {
        try {
            const [res] = await Promise.all([
                apiCall(
                    'post',
                    'updatePreference',
                    formData,
                    '',
                    'user/preference'
                ),
            ])
            resp.commonResponse(res.data, 'updatePreference')
        } catch (err) {
            resp.commonErrorResponse('updatePreference')
        }
    }

    // Log out
    const logout = useCallback(async () => {
        try {
            await apiCall('post', 'logout', {}, '', '')
        } catch (err) {
            // logout best-effort — always clear local state
        }
        localStorage.removeItem('token')
        setAuthToken(null)
        dispatch({
            type: LOGOUT,
        })
    }, [])

    useEffect(() => {
        setSessionTimeoutHandler(logout)
        return () => setSessionTimeoutHandler(null)
    }, [logout])

    // Clear Response
    const clearResponse = () =>
        dispatch({
            type: CLEAR_RESPONSE,
        })

    return (
        <AuthContext.Provider
            value={{
                token: state.token,
                isAuthenticated: state.isAuthenticated,
                isAdmin: state.isAdmin,
                loading: state.loading,
                user: state.user,
                twoFactor_details: state.twoFactor_details,
                responseStatus: state.responseStatus,
                register,
                updateProfile,
                updatePreference,
                get2faDetails,
                checkValidation,
                login,
                logout,
                loadUser,
                switchUser,
                clearResponse,
            }}
        >
            {props.children}
        </AuthContext.Provider>
    )
}

export default AuthState
